import { useQuery } from '@tanstack/react-query';
import qs from 'query-string';
import { opsApi } from '@/api/client';
import type { ResponseArray } from '@/types/shared';

/**
 * Operations — internal cross-org service management endpoints.
 * Served by @mgmt `/internal/ops/*`. Auth: the UMA RPT (opsApi client) — the
 * plain access token has no `groups` claim, so margarita's EmployeeRequired
 * (groups contains "/Admin") rejects it with 403; the RPT carries the groups.
 * All endpoints are GET-only.
 *
 * API contract:
 *   GET /internal/ops/services  → OpsServiceRow[]  (server-paginated)
 *   GET /internal/ops/summary   → OpsSummary
 */

/* ── Types ─────────────────────────────────────────────────────────────── */

/** Service categories returned by the backend. */
export type OpsCategory =
  | 'cdn'
  | 'waap'
  | 'storage'
  | 'media'
  | 'compute'
  | 'registrar'
  | 'other';

/** Service statuses the backend exposes on ops rows (models/service.go). */
export type OpsServiceStatus =
  | 'active'
  | 'suspended'
  | 'terminated'
  | 'archived';

/**
 * One row from `GET /internal/ops/services`.
 * Provider block and optional customer block are nested objects.
 */
export interface OpsProviderInfo {
  id?: string;
  name: string;
  /** Human-readable display name (may be absent on list rows). */
  display_name?: string;
  type: string;
  /** Animal-scope string, e.g. "Internal.Techev" or "External.Conversant.CDN". */
  legacy_scope: string;
  category: OpsCategory;
}

export interface OpsServiceRow {
  id: string;
  service_name: string;
  organization_id: string;
  status: OpsServiceStatus;
  created_at: string;
  /** Provider block — omitted by the backend (`omitempty`) when unresolved. */
  provider: OpsProviderInfo | null;
  /** Entity UIDs linked to this service (CDN domains, VM instances, etc.). */
  entity_uids: string[];
  /** Count of domains associated with this service. */
  domain_count: number;
  /** Customer enrichment — null when the org has no linked DataCentral record. */
  customer: OpsCustomerRow | null;
}

/**
 * Response from `GET /internal/ops/summary`.
 * Provides system-wide counts (unfiltered) for the stat banner.
 */
export interface OpsSummary {
  /** Count of distinct organizations with services. */
  distinct_orgs: number;
  /** Total linked domains across all services. */
  total_domains: number;
  /**
   * Per-category → per-status counts, e.g. `{waap: {active: 8, terminated: 1}}`.
   * The backend omits categories with 0 services and sends NO flat
   * total_services field — derive totals with the helpers below.
   */
  by_category: Partial<Record<OpsCategory, Record<string, number>>>;
}

/** Sum every status count of one category (missing category → 0). */
export function opsCategoryTotal(s: OpsSummary | undefined, cat: OpsCategory): number {
  const byStatus = s?.by_category?.[cat];
  if (!byStatus) return 0;
  return Object.values(byStatus).reduce((acc, n) => acc + n, 0);
}

/** Grand total of services across all categories and statuses. */
export function opsServicesTotal(s: OpsSummary | undefined): number {
  if (!s?.by_category) return 0;
  return (Object.keys(s.by_category) as OpsCategory[]).reduce(
    (acc, cat) => acc + opsCategoryTotal(s, cat),
    0,
  );
}

/* ── Params ────────────────────────────────────────────────────────────── */

export interface OpsServicesParams {
  page?: number;
  per_page?: number;
  /** Full-text search across service name, org, customer. */
  q?: string;
  category?: OpsCategory | '';
  /** Provider name filter (animal name, e.g. "raven"). */
  provider?: string;
  status?: OpsServiceStatus | '';
  /** Organization ID filter. */
  org_id?: number | '';
}

/* ── Query helpers ──────────────────────────────────────────────────────── */

function buildServicesQuery(params: OpsServicesParams): string {
  const clean: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '' && v !== null) {
      clean[k] = v as string | number;
    }
  }
  return qs.stringify(clean);
}

/* ── Query keys ─────────────────────────────────────────────────────────── */

/* ── Detail types ───────────────────────────────────────────────────────── */

/** Zone row in the service detail response (no credentials). */
export interface OpsZoneRow {
  id: string;
  zone_name: string;
  api_endpoint: string;
}

/** Service-zone row in the service detail response. */
export interface OpsServiceZoneRow {
  id: string;
  uid: string;
  status: string;
  zone?: OpsZoneRow;
  /**
   * Whether this zone has its own per-customer console credential (connected)
   * vs a shared reseller key (disconnected). Only present for managed-HMAC
   * providers (Eagle); undefined = not applicable.
   */
  connected?: boolean;
}

/** Result of POST /internal/ops/services/:id/cdn/connection. */
export interface OpsCdnConnectionResult {
  connected: boolean;
  already_connected?: boolean;
  entity_id?: string;
  provider?: string;
}

/**
 * Body of POST /internal/ops/services/:id/credential/rebind.
 * Provide either `from_service_id` or `username` + `password`.
 */
export interface OpsRebindCredentialPayload {
  reason: string;
  from_service_id?: string;
  username?: string;
  password?: string;
  /** Re-bind every live Raven service of the org (credentials are org-shared). */
  apply_to_org?: boolean;
  /** Re-points this service's zone uid to the chosen vEdge org (this service only). */
  vedge_org_id?: string;
  /** Create the vEdge login for `email` (or reset its password) and store it. Needs vedge_org_id. */
  provision?: boolean;
  email?: string;
}

/** Result of GET /internal/ops/raven/accounts/check. */
export interface OpsRavenAccount {
  exists: boolean;
  username: string;
  user_id?: number;
  role?: string;
  org_id?: number;
  org_name?: string;
  active?: boolean;
}

/** One vEdge organization from GET /internal/ops/raven/orgs. */
export interface OpsVEdgeOrg {
  id: number;
  name: string;
  slug: string;
  enabled: boolean;
  domain_count: number;
  user_count: number;
}

/** Result of POST /internal/ops/services/:id/credential/rebind. */
export interface OpsRebindCredentialResult {
  updated: number;
  service_ids: string[];
  /** Provision mode: whether the vEdge login was newly created or had its password reset. */
  account?: 'created' | 'reset';
}

/** One Eagle CDN service's console-connection state (fleet view). */
export interface OpsEagleConnection {
  service_id: string;
  organization_id: string;
  service_name: string;
  status: string;
  entity_id: string;
  connected: boolean;
  customer?: OpsCustomerRow | null;
}

/** Customer enrichment from DataCentral (null when org has no DC record). */
export interface OpsCustomerRow {
  customer_id: number;
  customer_name: string;
  customer_company_name?: string;
}

/** Full service detail from GET /internal/ops/services/:id. */
export interface OpsServiceDetail {
  id: string;
  service_name: string;
  organization_id: string;
  status: OpsServiceStatus;
  created_at: string;
  updated_at: string;
  provider: OpsProviderInfo | null;
  service_zones: OpsServiceZoneRow[];
  domain_count: number;
  customer: OpsCustomerRow | null;
}

/** Domain binding row from GET /internal/ops/services/:id/domains. */
export interface OpsDomainBinding {
  id: string;
  domain_name: string;
  domain_id: string;
  type: string;
  status: string;
  uid?: string;
  /** Raw JSONB — may contain waap_website_id, waap_service_id, etc. */
  metadata?: Record<string, unknown> | null;
  drift_detected_at?: string | null;
  drift_fields?: unknown[] | null;
  created_at: string;
  updated_at: string;
}

/* ── CDN analytics types (service-scoped proxy) ─────────────────────────── */

/** One time-series point from the CDN analytics store. */
export interface OpsCDNPoint {
  timestamp: string;
  value: number;
}

/** Full analytics query result from GET /internal/ops/services/:id/cdn/analytics. */
export interface OpsCDNSeries {
  metric: string;
  granularity: string;
  series: OpsCDNPoint[];
  by_provider?: Record<string, OpsCDNPoint[]>;
  by_dimension?: Record<string, OpsCDNPoint[]>;
}

/** Summary response from GET /internal/ops/services/:id/cdn/summary. */
export interface OpsCDNSummary {
  volume_bytes: number;
  peak_bandwidth_bps: number;
  peak_rps: number;
  domain_count: number;
  from: string;
  to: string;
}

/* ── CDN analytics params ────────────────────────────────────────────────── */

export interface OpsCDNAnalyticsParams {
  from: string;
  to: string;
  metric?: string;
  granularity?: string;
}

/* ── WAAP types ─────────────────────────────────────────────────────────── */

/**
 * Response from GET /internal/ops/services/:id/waap/package.
 * package_name is the portal-correct label (Basic/Standard/Pro/Ultimate) or
 * empty when no package is set.
 */
export interface OpsWAAPPackage {
  service_id: string;
  entity_id: string;
  package_id: string;
  package_name: string;
  cached: boolean;
  fetched_at: string;
}

/** Diff status for a single domain in the WAAP websites diff. */
export type WaapDomainStatus = 'matched' | 'store_only' | 'vendor_only';

/** One domain row in the WAAP websites diff. */
export interface OpsWAAPWebsiteRow {
  domain: string;
  source_status: WaapDomainStatus;
  vendor_website_id: number;
  vendor_service_id: number;
  store_website_id: number;
  store_binding_status: string;
  mismatch: boolean;
}

/** Response from GET /internal/ops/services/:id/waap/websites. */
export interface OpsWAAPWebsitesDiff {
  service_id: string;
  entity_id: string;
  vendor_count: number;
  store_count: number;
  domains: OpsWAAPWebsiteRow[];
  fetched_at: string;
}

export const opsKeys = {
  all: ['ops'] as const,
  summary: () => ['ops', 'summary'] as const,
  services: (params: OpsServicesParams) => ['ops', 'services', params] as const,
  service: (id: string) => ['ops', 'service', id] as const,
  serviceDomains: (id: string) => ['ops', 'service', id, 'domains'] as const,
  waapPackage: (id: string) => ['ops', 'service', id, 'waap', 'package'] as const,
  waapWebsites: (id: string) => ['ops', 'service', id, 'waap', 'websites'] as const,
  cdnAnalytics: (id: string, params: OpsCDNAnalyticsParams) =>
    ['ops', 'service', id, 'cdn', 'analytics', params] as const,
  cdnSummary: (id: string, params: Pick<OpsCDNAnalyticsParams, 'from' | 'to'>) =>
    ['ops', 'service', id, 'cdn', 'summary', params] as const,
  cdnConnections: (status: string) => ['ops', 'cdn', 'connections', status] as const,
};

/* ── Hooks ──────────────────────────────────────────────────────────────── */

/**
 * `GET /internal/ops/summary` — system-wide stat counts for the stat banner.
 * Always unfiltered (reflects full system state, not user's current filter).
 */
export function useOpsSummary() {
  return useQuery({
    queryKey: opsKeys.summary(),
    queryFn: async (): Promise<OpsSummary> => {
      const res = await opsApi.get<{ result: OpsSummary }>('/internal/ops/summary');
      return res.data.result;
    },
    // Summary changes infrequently; 60s stale time avoids unnecessary refetches.
    staleTime: 60_000,
  });
}

/**
 * `GET /internal/ops/services` — cross-org service list, server-paginated.
 * All filter/search/page params are forwarded to the backend.
 */
export function useOpsServices(params: OpsServicesParams = {}) {
  return useQuery({
    queryKey: opsKeys.services(params),
    queryFn: async (): Promise<ResponseArray<OpsServiceRow>> => {
      const res = await opsApi.get<ResponseArray<OpsServiceRow>>(
        `/internal/ops/services?${buildServicesQuery({ page: 1, per_page: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/**
 * `GET /internal/ops/services/:id` — full service detail including provider,
 * service zones (no credentials), and optional customer enrichment.
 */
export function useOpsService(id: string) {
  return useQuery({
    queryKey: opsKeys.service(id),
    queryFn: async (): Promise<OpsServiceDetail> => {
      const res = await opsApi.get<{ result: OpsServiceDetail }>(
        `/internal/ops/services/${id}`,
      );
      return res.data.result;
    },
    enabled: Boolean(id),
  });
}

/**
 * `GET /internal/ops/cdn/connections` — Eagle CDN fleet with console-connection
 * status. `status` filters to 'connected' | 'disconnected' (empty = all).
 */
export function useOpsCdnConnections(status: string = '') {
  return useQuery({
    queryKey: opsKeys.cdnConnections(status),
    queryFn: async (): Promise<OpsEagleConnection[]> => {
      const q = status ? `?status=${status}` : '';
      const res = await opsApi.get<ResponseArray<OpsEagleConnection>>(
        `/internal/ops/cdn/connections${q}`,
      );
      return res.data.results ?? [];
    },
  });
}

/**
 * `GET /internal/ops/services/:id/domains` — domain bindings for one service,
 * including metadata (vendor ids) and drift detection fields.
 */
export function useOpsServiceDomains(id: string) {
  return useQuery({
    queryKey: opsKeys.serviceDomains(id),
    queryFn: async (): Promise<OpsDomainBinding[]> => {
      const res = await opsApi.get<{ results: OpsDomainBinding[] }>(
        `/internal/ops/services/${id}/domains`,
      );
      return res.data.results ?? [];
    },
    enabled: Boolean(id),
  });
}

/**
 * `GET /internal/ops/services/:id/cdn/analytics` — CDN bandwidth time-series
 * proxied through the employee-auth ops route (employee JWT, no service-auth token).
 */
export function useOpsCDNAnalytics(id: string, params: OpsCDNAnalyticsParams) {
  return useQuery({
    queryKey: opsKeys.cdnAnalytics(id, params),
    queryFn: async (): Promise<OpsCDNSeries> => {
      const p = new URLSearchParams({ from: params.from, to: params.to });
      if (params.metric) p.set('metric', params.metric);
      if (params.granularity) p.set('granularity', params.granularity);
      // response.OK wraps: { status: 200, data: <QueryResult> }
      const res = await opsApi.get<{ status: number; data: OpsCDNSeries }>(
        `/internal/ops/services/${id}/cdn/analytics?${p.toString()}`,
      );
      return res.data.data;
    },
    enabled: Boolean(id) && Boolean(params.from) && Boolean(params.to),
  });
}

/**
 * `GET /internal/ops/services/:id/cdn/summary` — CDN usage KPIs (total volume,
 * peak bandwidth, peak RPS, domain count) for the given window.
 */
export function useOpsCDNSummary(
  id: string,
  params: Pick<OpsCDNAnalyticsParams, 'from' | 'to'>,
) {
  return useQuery({
    queryKey: opsKeys.cdnSummary(id, params),
    queryFn: async (): Promise<OpsCDNSummary> => {
      const p = new URLSearchParams({ from: params.from, to: params.to });
      // response.OK wraps: { status: 200, data: { volume_bytes, ... } }
      const res = await opsApi.get<{ status: number; data: OpsCDNSummary }>(
        `/internal/ops/services/${id}/cdn/summary?${p.toString()}`,
      );
      return res.data.data;
    },
    enabled: Boolean(id) && Boolean(params.from) && Boolean(params.to),
  });
}

/**
 * `GET /internal/ops/services/:id/waap/package` — vendor subscription package.
 * Cached 8h on the server; pass refresh=true to force a live vendor call.
 * Not auto-fetched (enabled=false by default) — caller triggers via refetch()
 * so the ops console never fires vendor calls on page mount fleet-wide.
 */
export function useOpsWAAPPackage(id: string, opts?: { enabled?: boolean; refresh?: boolean }) {
  const enabled = opts?.enabled ?? false;
  const refresh = opts?.refresh ?? false;
  return useQuery({
    queryKey: [...opsKeys.waapPackage(id), { refresh }] as const,
    queryFn: async (): Promise<OpsWAAPPackage> => {
      const url = refresh
        ? `/internal/ops/services/${id}/waap/package?refresh=1`
        : `/internal/ops/services/${id}/waap/package`;
      const res = await opsApi.get<OpsWAAPPackage>(url);
      return res.data;
    },
    enabled: Boolean(id) && enabled,
    // Do not stale-refetch automatically — package data is on-demand only.
    staleTime: Infinity,
  });
}

/**
 * `GET /internal/ops/services/:id/waap/websites` — vendor WAAP website list
 * with store diff. Not auto-fetched (enabled=false by default).
 */
export function useOpsWAAPWebsites(id: string, opts?: { enabled?: boolean }) {
  const enabled = opts?.enabled ?? false;
  return useQuery({
    queryKey: opsKeys.waapWebsites(id),
    queryFn: async (): Promise<OpsWAAPWebsitesDiff> => {
      const res = await opsApi.get<OpsWAAPWebsitesDiff>(
        `/internal/ops/services/${id}/waap/websites`,
      );
      return res.data;
    },
    enabled: Boolean(id) && enabled,
    staleTime: Infinity,
  });
}

// Types and hooks for compute/storage/media category endpoints live in:
// @/api/operations-category-hooks
export type {
  OpsInstanceRow,
  OpsStorageSummary,
  OpsStorageBucketRow,
  OpsStorageBusiness,
  OpsMediaPoint,
  OpsStorageAnalyticsPoint,
  OpsMediaSeries,
  OpsStorageAnalyticsSeries,
} from './operations-category-hooks';
export {
  useOpsServiceInstances,
  useOpsStorageBusiness,
  useOpsStorageAnalytics,
  useOpsMediaAnalytics,
} from './operations-category-hooks';
